// frontend/src/components/assessment/AssessmentPrintDocument.js
//
// Hidden document rendered alongside the assessment UI so the header's
// print button can produce a PDF (via window.print()) containing the
// situation, script, and debrief — regardless of which artifact tab is
// currently open. Modeled on roleplay/RoleplayPrintDocument.js.

import PropTypes from 'prop-types';
import ConversationReport from './artifacts/AssessmentForm';

const SITUATION_FIELDS = [
  { label: 'Counterpart',        getValue: cs => cs?.characterBrief?.inSum?.content },
  { label: 'Real Issue',  getValue: cs => cs?.realIssue?.summary?.content },
  { label: 'Data & Reasoning',   getValue: cs => cs?.realIssue?.dataAndReasoning?.content },
  { label: 'Stakes — Downsides', getValue: cs => cs?.characterStakes?.downSides?.inSum?.content },
  { label: 'Stakes — Upsides',   getValue: cs => cs?.characterStakes?.upSides?.inSum?.content },
  { label: 'Desired Outcome',    getValue: cs => cs?.desiredOutcome?.content },
  { label: 'Challenge',          getValue: cs => cs?.challenge?.content },
];

const AssessmentPrintDocument = ({ conversationTitle, caseStudy, script = [], report, includeDebrief, learningGoal, debriefUnavailableMessage }) => (
  <div className="assessment-print-root">
    <h1 className="rp-print-doc-title">{conversationTitle || 'Assessment'}</h1>

    <section className="rp-print-section">
      <h2 className="rp-print-section-title">Situation</h2>
      {caseStudy ? (
        <div className="rp-script-list">
          {SITUATION_FIELDS.map(({ label, getValue }) => {
            const value = getValue(caseStudy);
            if (!value) return null;
            return (
              <div key={label} className="rp-impl-cs-section">
                <div className="rp-impl-cs-label">{label}</div>
                <div className="rp-impl-cs-value">{value}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rp-script-empty">Situation not available.</p>
      )}
    </section>

    {learningGoal && (
      <section className="rp-print-section">
        <h2 className="rp-print-section-title">Learning Goal</h2>
        <p className="rp-impl-cs-value">{learningGoal}</p>
      </section>
    )}

    <section className="rp-print-section rp-print-page-break">
      <h2 className="rp-print-section-title">Script</h2>
      {script.length === 0 ? (
        <p className="rp-script-empty">No script captured.</p>
      ) : (
        <div>
          {script.map((pair, i) => (
            <div key={pair.pairId || i} className="im-pair">
              <div className="im-turn-row">
                <div className="im-thought-col">
                  <div className="im-label-col">
                    <span className="script-speaker-label im-thought-label">thoughts:</span>
                  </div>
                  <div className="im-readonly-content im-thought-readonly-text">
                    {pair.me_thought?.content || <span className="im-readonly-empty">—</span>}
                  </div>
                </div>
                <div className="im-turn-col im-turn-me">
                  <span className="script-speaker-label script-speaker-me">me:</span>
                  <div className="im-readonly-content">
                    {pair.me?.content || <span className="im-readonly-empty">—</span>}
                  </div>
                </div>
              </div>

              <div className="im-turn-row">
                <div className="im-thought-col">
                  <div className="im-label-col">
                    <span className="script-speaker-label im-thought-label">thoughts:</span>
                  </div>
                  <div className="im-readonly-content im-thought-readonly-text">
                    {pair.them_thought?.content || <span className="im-readonly-empty">—</span>}
                  </div>
                </div>
                <div className="im-turn-col im-turn-them">
                  <span className="script-speaker-label script-speaker-them">them:</span>
                  <div className="im-readonly-content">
                    {pair.them?.content || <span className="im-readonly-empty">—</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>

    {includeDebrief && report ? (
      <section className="rp-print-section rp-print-page-break">
        <h2 className="rp-print-section-title">Debrief</h2>
        <ConversationReport data={report} />
      </section>
    ) : debriefUnavailableMessage ? (
      <section className="rp-print-section rp-print-page-break">
        <h2 className="rp-print-section-title">Debrief</h2>
        <p className="rp-script-empty">{debriefUnavailableMessage}</p>
      </section>
    ) : null}
  </div>
);

AssessmentPrintDocument.propTypes = {
  conversationTitle: PropTypes.string,
  caseStudy: PropTypes.object,
  script: PropTypes.arrayOf(
    PropTypes.shape({
      pairId: PropTypes.string,
      me: PropTypes.shape({ content: PropTypes.string }),
      them: PropTypes.shape({ content: PropTypes.string }),
      me_thought: PropTypes.shape({ content: PropTypes.string }),
      them_thought: PropTypes.shape({ content: PropTypes.string }),
    })
  ),
  report: PropTypes.object,
  includeDebrief: PropTypes.bool,
  learningGoal: PropTypes.string,
  debriefUnavailableMessage: PropTypes.string,
};

export default AssessmentPrintDocument;
